// Scene: Venn
// Four-phase, 20-second animation:
//   Birth   (0–2 s)  — all three circles emerge from the canvas centre
//   Explore (2–10 s) — each circle drifts outward along a random route
//   Converge(10–17s) — circles ease into the classic three-circle Venn layout
//   Hold    (17–20s) — static Venn diagram (RGB overlaps produce CMY + white)
//
// Random waypoints are generated on createScene, so every mount produces a
// unique journey to the same deterministic final state.

const COLORS = [
  { r: 255, g: 0,   b: 0   }, // Red
  { r: 0,   g: 255, b: 0   }, // Green
  { r: 0,   g: 0,   b: 255 }, // Blue
];

// Phase boundaries in milliseconds
const T_BIRTH_END    =  2_000;
const T_EXPLORE_END  = 10_000;
const T_CONVERGE_END = 17_000;
export const DURATION = 20_000;

// Ease-in-out cubic (smoothstep)
function easeInOut(t) {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

// Linear progress through a sub-range of t, clamped to [0, 1]
function phaseP(t, start, end) {
  return Math.max(0, Math.min(1, (t - start) / (end - start)));
}

export function createVennScene(w, h) {
  const minDim = Math.min(w, h);
  const cx = w * 0.5;
  const cy = h * 0.5;

  // ── Final Venn positions ──────────────────────────────────────────────────
  // Equilateral triangle where the centre-to-centre distance equals the
  // circle radius → clearly visible pairwise and triple overlaps.
  const FINAL_RADIUS = minDim * 0.28;
  // Circumradius of the equilateral triangle (distance from centroid to vertex)
  const R = FINAL_RADIUS / Math.sqrt(3);

  // Angles: Red=up, Green=lower-left, Blue=lower-right (canvas y-down coords)
  const finalPositions = [
    { cx: cx,             cy: cy - R         }, // Red
    { cx: cx - R * 0.866, cy: cy + R * 0.5   }, // Green
    { cx: cx + R * 0.866, cy: cy + R * 0.5   }, // Blue
  ];

  // ── Random exploration waypoints ─────────────────────────────────────────
  // Each circle explores a sector broadly aligned with its final direction,
  // with ±60° variation so paths feel random but never cross or cluster.
  const BASE_ANGLES = [
    -Math.PI / 2,                        // Red   → up
    -Math.PI / 2 + (4 * Math.PI) / 3,   // Green → lower-left  (150°)
    -Math.PI / 2 + (2 * Math.PI) / 3,   // Blue  → lower-right (30°)
  ];

  const waypoints = BASE_ANGLES.map((baseAngle) => {
    const angle    = baseAngle + (Math.random() - 0.5) * (Math.PI / 1.5); // ±60°
    const distance = minDim * (0.28 + Math.random() * 0.18);              // 28–46% of minDim
    return {
      cx: cx + Math.cos(angle) * distance,
      cy: cy + Math.sin(angle) * distance,
    };
  });

  return {
    duration: DURATION,

    getCircles(t) {
      // Clamp so the scene holds its final state after DURATION
      const tc = Math.min(t, DURATION);

      return COLORS.map((color, i) => {
        const final   = finalPositions[i];
        const waypoint = waypoints[i];
        let circleCx, circleCy, radius;

        if (tc < T_BIRTH_END) {
          // Phase 1: Birth — emerge from canvas centre, tiny radius grows
          const p = easeInOut(phaseP(tc, 0, T_BIRTH_END));
          circleCx = cx;
          circleCy = cy;
          radius   = minDim * 0.05 * p;

        } else if (tc < T_EXPLORE_END) {
          // Phase 2: Explore — drift outward toward random waypoint, grow large
          const p = easeInOut(phaseP(tc, T_BIRTH_END, T_EXPLORE_END));
          circleCx = lerp(cx, waypoint.cx, p);
          circleCy = lerp(cy, waypoint.cy, p);
          radius   = minDim * lerp(0.05, 0.34, p);

        } else if (tc < T_CONVERGE_END) {
          // Phase 3: Converge — ease from waypoint into final Venn position
          const p = easeInOut(phaseP(tc, T_EXPLORE_END, T_CONVERGE_END));
          circleCx = lerp(waypoint.cx, final.cx, p);
          circleCy = lerp(waypoint.cy, final.cy, p);
          radius   = minDim * lerp(0.34, FINAL_RADIUS / minDim, p);

        } else {
          // Phase 4: Hold — static Venn diagram
          circleCx = final.cx;
          circleCy = final.cy;
          radius   = FINAL_RADIUS;
        }

        return { ...color, cx: circleCx, cy: circleCy, radius };
      });
    },
  };
}
